import { useState, useEffect } from 'react';
import api from '../services/api';

const retentionColors = {
  high: 'bg-green-100 text-green-800',   // >= target
  mid: 'bg-amber-50 text-amber-800',     // >= industry
  low: 'bg-red-50 text-red-700',         // < industry
  na: 'bg-teacher-bg text-teacher-text-tertiary',        // no data
};

function retentionClass(value, day) {
  if (value === null || value === undefined) return retentionColors.na;
  const targets = { d1: 0.85, d3: 0.72, d7: 0.55, d14: 0.40, d30: 0.28 };
  const industries = { d1: 0.65, d3: 0.50, d7: 0.30, d14: 0.20, d30: 0.12 };
  if (value >= (targets[day] || 0.5)) return retentionColors.high;
  if (value >= (industries[day] || 0.2)) return retentionColors.mid;
  return retentionColors.low;
}

export default function RetentionPage() {
  const [cohorts, setCohorts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        // Fetch from the retention endpoint (reads metrics_cohorts collection)
        const result = await api.getRetentionCohorts();
        setCohorts(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading retention: {error}
        </div>
      </div>
    );
  }

  const cohortList = cohorts?.cohorts || [];
  const latestCohort = cohortList[cohortList.length - 1];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-teacher-text-primary">Retention</h1>
        <p className="text-teacher-text-secondary">Cohort retention curves from <code className="text-xs bg-teacher-bg-secondary px-1 rounded">metrics_cohorts</code></p>
      </div>

      {/* Summary metrics */}
      {latestCohort && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {['d1', 'd7', 'd14', 'd30'].map(day => {
            const value = latestCohort.retention?.[day];
            const color = retentionClass(value, day);
            return (
              <div key={day} className={`rounded-xl p-6 shadow-sm border border-teacher-border border-l-4 ${value >= 0.5 ? 'border-l-green-500' : value >= 0.2 ? 'border-l-amber-500' : 'border-l-red-500'} bg-white`}>
                <h3 className="text-sm font-medium text-teacher-text-secondary mb-1">{day.toUpperCase()} Retention</h3>
                <span className="text-3xl font-bold text-teacher-text-primary">
                  {value != null ? `${Math.round(value * 100)}%` : '—'}
                </span>
                <p className="text-xs text-teacher-text-tertiary mt-1">Latest cohort ({latestCohort.cohort_week})</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Cohort heatmap table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border mb-6">
        <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">Cohort Retention Heatmap</h3>
        {loading ? (
          <div className="h-48 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : cohortList.length === 0 ? (
          <div className="text-center py-12 text-teacher-text-tertiary">
            <p className="text-lg mb-2">No cohort data yet</p>
            <p className="text-sm">Cohort retention is computed weekly by the Sunday cron job.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-teacher-border">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Cohort</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Users</th>
                  {['D1', 'D3', 'D7', 'D14', 'D30'].map(d => (
                    <th key={d} className="text-center py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohortList.map(c => (
                  <tr key={c.cohort_week} className="border-b border-gray-50">
                    <td className="py-2 px-3 font-semibold text-teacher-text-primary">{c.cohort_week}</td>
                    <td className="py-2 px-3 text-center text-teacher-text-secondary">{c.signup_count}</td>
                    {['d1', 'd3', 'd7', 'd14', 'd30'].map(day => {
                      const val = c.retention?.[day];
                      return (
                        <td key={day} className="py-2 px-3 text-center">
                          <span className={`inline-block px-2.5 py-1 rounded text-xs font-semibold ${retentionClass(val, day)}`}>
                            {val != null ? `${Math.round(val * 100)}%` : '—'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Retention by source + Aha impact */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By source */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
          <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">Retention by Source</h3>
          {latestCohort?.by_source ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-teacher-border">
                  <th className="text-left py-2 text-xs font-semibold text-teacher-text-secondary uppercase">Source</th>
                  <th className="text-center py-2 text-xs font-semibold text-teacher-text-secondary uppercase">Users</th>
                  <th className="text-center py-2 text-xs font-semibold text-teacher-text-secondary uppercase">D7</th>
                  <th className="text-center py-2 text-xs font-semibold text-teacher-text-secondary uppercase">D30</th>
                </tr>
              </thead>
              <tbody>
                {['coaching', 'organic', 'reel'].map(src => {
                  const data = latestCohort.by_source[src];
                  if (!data || data.signup_count === 0) return null;
                  return (
                    <tr key={src} className="border-b border-gray-50">
                      <td className="py-2 font-medium text-teacher-text-primary capitalize">{src}</td>
                      <td className="py-2 text-center text-teacher-text-secondary">{data.signup_count}</td>
                      <td className="py-2 text-center">
                        <span className={`font-semibold ${(data.d7 || 0) >= 0.5 ? 'text-green-600' : 'text-amber-600'}`}>
                          {data.d7 != null ? `${Math.round(data.d7 * 100)}%` : '—'}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className={`font-semibold ${(data.d30 || 0) >= 0.25 ? 'text-green-600' : 'text-red-600'}`}>
                          {data.d30 != null ? `${Math.round(data.d30 * 100)}%` : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-teacher-text-tertiary text-sm">No source data available yet</p>
          )}
        </div>

        {/* Aha moment impact */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
          <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">Aha Moment Impact on Retention</h3>
          {latestCohort?.aha_moment_impact ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-xs font-semibold text-green-700 mb-1">Reached Aha</div>
                  <div className="text-2xl font-bold text-green-700">
                    {latestCohort.aha_moment_impact.users_who_hit_aha?.d30 != null
                      ? `${Math.round(latestCohort.aha_moment_impact.users_who_hit_aha.d30 * 100)}%`
                      : '—'}
                  </div>
                  <div className="text-xs text-teacher-text-secondary">
                    D30 ({latestCohort.aha_moment_impact.users_who_hit_aha?.count || 0} users)
                  </div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-xs font-semibold text-red-700 mb-1">Did Not Reach Aha</div>
                  <div className="text-2xl font-bold text-red-700">
                    {latestCohort.aha_moment_impact.users_who_did_not?.d30 != null
                      ? `${Math.round(latestCohort.aha_moment_impact.users_who_did_not.d30 * 100)}%`
                      : '—'}
                  </div>
                  <div className="text-xs text-teacher-text-secondary">
                    D30 ({latestCohort.aha_moment_impact.users_who_did_not?.count || 0} users)
                  </div>
                </div>
              </div>
              <p className="text-xs text-teacher-text-tertiary bg-amber-50 px-3 py-2 rounded">
                Correlational — not causal. Users who engage with Atlas may differ in baseline motivation.
              </p>
            </>
          ) : (
            <p className="text-teacher-text-tertiary text-sm">No aha impact data available yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
